import { z, isoZ } from 'my-zod'
import { AddOnProductType } from '../add-on-product'

export const addOnMetadata = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('AdHoc'),
    notes: z.string(),
    taxPolicyIds: z.array(z.number())
  }),
  z.object({
    type: z.literal('CancellationFee')
  }),
  z.object({
    type: z.literal('DamageFee')
  }),
  z.object({
    type: z.literal('AddOnProduct'),
    productId: z.string().uuid(),
    productVersionId: z.number(),
    productType: AddOnProductType
  }),
  z.object({
    type: z.literal('SensibleWeatherGuarantee'),
    name: z.string(),
    guaranteeId: z.string(),
    quoteId: z.string(),
    coverageStartInstant: isoZ.instant(),
    coverageStartDateTime: isoZ.dateTime(),
    coverageEndDateTime: isoZ.dateTime(),
    totalCoverageAmount: z.number()
  }),
  z.object({
    type: z.literal('RideInsurance'),
    versionId: z.number(),
    program: z.enum(['damageProtectionPlan', 'adventuresAssure']),
    planType: z.enum(['Standard', 'Premium']),
    coverageDescription: z.string()
  }),
  z.object({
    type: z.literal('GiftCard'),
    giftCardId: z.string()
  })
])

export type AddOnType = z.infer<typeof addOnMetadata>['type']
export type addonMetadata<T extends z.infer<typeof addOnMetadata>['type'] = z.infer<typeof addOnMetadata>['type']> = Extract<
  z.infer<typeof addOnMetadata>,
  { type: T }
>

export const addOnModel = z.object({
  id: z.string().uuid(),
  shortId: z.string(),
  customerOrderId: z.string().uuid().nullable(),
  reservationId: z.string().uuid().nullable(),
  metadata: addOnMetadata,
  createdDate: isoZ.instant(),
  lastModifiedDate: isoZ.instant(),
  deletedDate: isoZ.instant().nullish(),
  cancellationDate: isoZ.instant().nullish()
})

export type addOnModel = z.infer<typeof addOnModel>

export type addOnGeneric<T extends z.infer<typeof addOnMetadata>['type']> = Omit<addOnModel, 'metadata'> & {
  metadata: addonMetadata<T>
}

/**
 * This function is mostly here to help with typescript types inferring correctly. Use as
 *
 * ```ts
 * transfers.filter(isTransferOfType('StripePayment'))
 * ```
 */
export function isAddonOfTypeFilter<Addon extends { metadata: addonMetadata }, Type extends AddOnType>(type: Type) {
  return function (addon: Addon): addon is Addon & { metadata: addonMetadata<Type> } {
    return addon.metadata.type === type
  }
}
